import api from './api';

/**
 * #696: Reconciliation > Insurer ledger and Ledger match. Read-only. Amounts are API decimal strings.
 * Financial year only (1 Apr - 31 Mar), e.g. "2026-27".
 */

const BASE = '/commissions/ledger-match';

export type MatchFlag = 'no_posp_line' | 'no_insurer_commission' | 'received_unpaid' | 'paid_not_received';

export const FLAG_TITLE: Record<MatchFlag, string> = {
  no_posp_line: 'Insurer commission, no POSP payable line',
  no_insurer_commission: 'POSP line, no insurer commission',
  received_unpaid: 'Received from the insurer, POSP not paid',
  paid_not_received: 'POSP paid, not received from the insurer',
};

export interface MatchParams {
  financial_year?: string;
  month?: string;
  insurer?: string;
  posp?: string;
  flag?: MatchFlag | '';
  offset?: number;
  limit?: number;
}

export interface MatchRow {
  id: string;
  date: string;
  policy_id: string | null;
  policy_number: string | null;
  customer: string | null;
  insurer_id: string | null;
  insurer_name: string | null;
  orphan: boolean;
  posps: { id: string; code: string; name: string }[];
  no_posp?: boolean;
  insurer_commission: string;
  posp_share: string;
  broker_margin: string;
  insurer_received: string;
  insurer_tds: string;
  insurer_outstanding: string;
  posp_paid: string;
  posp_outstanding: string;
  flags: MatchFlag[];
}

export interface MatchTotals {
  insurer_commission: string;
  posp_share: string;
  broker_margin: string;
  insurer_received: string;
  insurer_tds: string;
  insurer_outstanding: string;
  posp_paid: string;
  posp_outstanding: string;
}

export interface InsurerTotals extends MatchTotals {
  insurer: string | null;
  insurer_name: string;
  policies: number;
  mismatches: number;
  received_no_policy: string;
}

export interface PospTotals {
  posp: string | null; // null: the (no POSP) line
  posp_code: string;
  posp_name: string;
  lines: number;
  posp_share: string;
  posp_paid: string;
  posp_outstanding: string;
}

export interface MatchData {
  financial_year: string;
  financial_years: string[];
  date_from: string;
  date_to: string;
  month: string | null;
  cards: { flag: MatchFlag; title: string; count: number }[];
  totals: MatchTotals;
  by_insurer: InsurerTotals[];
  by_posp: PospTotals[];
  count: number;
  offset: number;
  limit: number;
  results: MatchRow[];
}

export interface InsurerStatementRow {
  id: string;
  date: string;
  entry_type: 'earned' | 'received' | 'tds';
  entry_label: string;
  policy_id: string | null;
  policy_number: string | null;
  customer: string | null;
  reference: string | null;
  debit: string | null;
  credit: string | null;
  running_balance: string;
  running_text: string;
  earned: string | null;
  received: string | null;
  tds: string | null;
  outstanding: string | null;
}

export interface InsurerStatement {
  insurer: { id: string; name: string };
  financial_year: string;
  financial_years: string[];
  date_from: string;
  date_to: string;
  balance_bf: { label: string; date: string; amount: string; text: string };
  balance_cf: { label: string; date: string; amount: string; text: string };
  totals: Record<'debit' | 'credit' | 'earned' | 'received' | 'tds', string>;
  outstanding_total: string;
  on_account: string;
  note: string;
  count: number;
  results: InsurerStatementRow[];
}

const clean = (p: object) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== '' && v != null));

function save(blob: Blob, name: string) {
  const href = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = href;
  link.download = name;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(href);
}

export const ledgerMatchService = {
  async match(params: MatchParams): Promise<MatchData> {
    return (await api.get(`${BASE}/match/`, { params: clean(params) })).data;
  },
  async exportMatch(params: MatchParams): Promise<void> {
    const r = await api.get(`${BASE}/match/`, { params: { ...clean(params), export: 'csv' }, responseType: 'blob' });
    save(r.data as Blob, `ledger-match-${params.financial_year || 'current'}${params.flag ? `-${params.flag}` : ''}.csv`);
  },
  async insurerStatement(insurer: string, financialYear: string): Promise<InsurerStatement> {
    return (await api.get(`${BASE}/insurer-statement/`, { params: { insurer, financial_year: financialYear } })).data;
  },
  async exportInsurerStatement(insurer: string, financialYear: string, kind: 'csv' | 'pdf'): Promise<void> {
    const r = await api.get(`${BASE}/insurer-statement/`, {
      params: { insurer, financial_year: financialYear, export: kind },
      responseType: 'blob',
    });
    save(r.data as Blob, `insurer-ledger-${financialYear}.${kind}`);
  },
};
